// @flow
import * as React from "react";
import { withProps } from "recompose";
import { renderToStaticMarkup } from "react-dom/server";
import { connect } from "react-redux";
import {
  branch,
  compose,
  renderComponent,
  withHandlers,
  withPropsOnChange,
} from "recompose";
import {
  anyPass,
  applySpec,
  equals,
  groupBy,
  includes,
  indexBy,
  isEmpty,
  map,
  partition,
  path,
  pathEq,
  pipe,
  prop,
  propEq,
  range,
  toPairs,
  uniq,
  trim,
  pick,
  filter as filterRamda,
  drop,
  isNil,
  findIndex,
  pathOr,
  hasPath,
} from "ramda";
import { Maybe } from "ramda-fantasy";

import { withI18n, type I18n } from "@quantum/i18n";
import { LinkButton, Spinner, withModalTrigger } from "@quantum/ui";

import { ClipboardModule } from "@ag-grid-enterprise/all-modules";

import {
  aggregationFunctions,
  defaultColDef,
  ID_COLUNA_GRUPO_AGGRID,
  ID_COLUNA_GRUPO_AXIS,
  MODO_APP_SERIES,
  MODO_EXIBICAO_MEDIDA_COMPOSTA,
  IDENTIFICADOR_SERIE_CNPJ,
  isNameColumn,
  type AssetKey,
  type ReportCol,
  type ReportGridRow,
  type ReportId,
  CASA_DECIMAL_PADRAO_NORMAL,
  CASA_DECIMAL_PADRAO_SERIES,
  ajustarCasasDecimais,
  colunaExibeMinimoCasasDecimais,
} from "../../../model";

import {
  exportReportAsCSV,
  exportReportAsExcel,
  exportarExcelVinculado,
  getGridState,
  getIsAxis,
  openAssetMenu,
  openGlobalSearch,
  openNewTask,
  removeAssets,
  saveAssetGroup,
  sendMail,
} from "../../../state";

import {
  isGeneratingReport,
  isLoadingSelectedReport,
  hasLoadedSelectedReport,
  saveGridState,
  selectedReportCols,
  selectedReportGridRows,
  selectedReportId,
  identificadoresSeries,
  getModoApp,
  getEscalaMoedas,
  i18n,
  exibirDatasSemNegociacaoSeries,
} from "../../../state";

import {
  debounce,
  formatCNPJ,
  formatDate,
  formatNumber,
  maybeEmpty,
  maybeParseFloat,
  parseDate,
  isEmptyLine,
  getAssetsWithoutResults,
  zeroToString,
  removePad,
  escalaFormatacao,
} from "../../../util";

import { AgGridReact } from "ag-grid-react";
import { Filters } from "../";
import Header from "../header";

import AggregationStatusBar from "./aggregation-status-bar";
import { createColumn } from "./columns";
import FormattedNumberFilter from "./formatted-number-filter";
import NameCell from "./name-cell";
import ProxyFloatingFilter from "./proxy-floating-filter";

import "./grid.css";

import Big from "big.js";

export { default as TimeSeriesGrid } from "./time-series-grid";

type Props = {
  loaded: boolean,
  loading: boolean,
  cols: Array<ReportCol>,
  rows: Array<ReportGridRow>,
  colHasMenuGroupItem: number => boolean,
  reportId: ReportId,
  onOpenNewTask: Function,
  onRemoveAsset: AssetKey => void,
  onRemoveAssets: (Array<AssetKey>) => void,
};

type State = {
  currentPage: number,
  pageCount: number,
  pageSize: number,
  pagination: boolean,
  selectedAssets: Array<AssetKey>,
};

const enhance = compose(
  withI18n,
  withModalTrigger,

  connect(
    applySpec({
      cols: selectedReportCols,
      colsComMedidas: selectedReportCols,
      rows: selectedReportGridRows,
      gridState: getGridState,
      reportId: selectedReportId,
      loading: isLoadingSelectedReport,
      generating: isGeneratingReport,
      loaded: hasLoadedSelectedReport,
      isInsideAxis: getIsAxis,
      identificadores: compose(ids => new Set(ids), identificadoresSeries),
      modoApp: getModoApp,
      exibirDatasSemNegociacao: exibirDatasSemNegociacaoSeries,
      escalasMoedas: getEscalaMoedas,
      i18n,
    }),
    {
      exportReportAsCSV,
      exportReportAsExcel,
      exportarExcelVinculado,
      removeAssets,
      saveAssetGroup,
      sendMail,
      onOpenAssetMenu: openAssetMenu,
      onOpenNewTask: openNewTask,
      openGlobalSearch,
      saveGridState,
    }
  ),

  branch(
    anyPass([prop("loading"), prop("generating")]),
    renderComponent(() => (
      <div className="Grid Grid__loading">
        <Spinner />
      </div>
    ))
  ),

  withHandlers({
    onOpenGlobalSearchLinkClick: ({ openGlobalSearch }) => event =>
      openGlobalSearch(event.target.value),
    onRemoveAsset: ({ removeAssets, reportId }) => assetKey =>
      removeAssets(reportId, [assetKey]),
    onRemoveAssets: ({ removeAssets, reportId }) => assetKeys =>
      removeAssets(reportId, assetKeys),
    colHasMenuGroupItem: ({ cols }) => colIndex =>
      pathEq([colIndex, "type"], "string", cols),
  }),

  withPropsOnChange(
    ["cols"],
    ({
      isInsideAxis,
      cols,
      rows,
      modoApp,
      onOpenAssetMenu,
      onOpenGlobalSearchLinkClick,
      escalasMoedas,
      i18n,
    }) => ({
      cols: cols
        .map((col, i) => {
          const getter = path(["data", "values", i]);

          const geraEscalaNaDescricao = medida => {
            const escala = pathOr(null, ["escala"], medida);
            const modoExibicao = pathOr(
              null,
              ["medida", "medida", "modoExibicao"],
              medida
            );
            return modoExibicao === MODO_EXIBICAO_MEDIDA_COMPOSTA ||
              typeof escala === "undefined" ||
              escala === null ||
              escala === 0
              ? ""
              : " " +
                  i18n.translate("preposicaoEm") +
                  " " +
                  i18n.translate(escalasMoedas[escala].chaveTraducao);
          };

          if (colunaExibeMinimoCasasDecimais(col)) {
            ajustarCasasDecimais(
              col,
              rows.map(row => row.values[i])
            );
          }

          const casasDecimaisDefault =
            modoApp === MODO_APP_SERIES
              ? CASA_DECIMAL_PADRAO_SERIES
              : CASA_DECIMAL_PADRAO_NORMAL;

          return {
            chartDataType: "series",
            enableValue: true,
            field: String(col.alias || i),
            valueGetter: params =>
              maybeEmpty(zeroToString(getter(params))).getOrElse(""),
            headerName: col.label + geraEscalaNaDescricao(col),
            enableRowGroup: true,
            checkboxSelection: i === 0,
            minWidth: 50,
            pinned: isNameColumn(col.medida) && "left",
            sortable: true,
            cellClass: !isNameColumn(col.medida) && "Grid__cell--centered",
            cellRendererFramework: params => {
              const { node, value, valueFormatted } = params;

              if (col.globalSearch && isInsideAxis) {
                return (
                  <LinkButton
                    className="Grid__cell--clickable"
                    value={value}
                    onClick={onOpenGlobalSearchLinkClick}
                  >
                    {value}
                  </LinkButton>
                );
              } else if (
                isNameColumn(col.medida) &&
                !node.group &&
                isInsideAxis
              ) {
                return (
                  <NameCell
                    assetKey={node.data.key}
                    value={value}
                    onOpenAssetMenu={onOpenAssetMenu}
                  />
                );
              } else {
                // Formata a célula aqui caso o valor passado possua __metadados__
                // Usado somente em casos em que a formatação
                // da coluna não se aplica especificamente para essa célula.
                // Importante: Se as regras de formatação de coluna mudarem, aqui também
                //             deve sofrer alterações!

                const parseValueOrJson = testedVar => {
                  try {
                    return testedVar.__metadados__;
                  } catch {
                    return value;
                  }
                };

                const normalizaValor = ({ valor, formatacaoResultado }) => {
                  switch (formatacaoResultado.formatacao) {
                    case "PORCENTAGEM_TAXAS":
                      return maybeEmpty(String(valor).trim())
                        .map(value =>
                          value
                            .replace("%", "")
                            .replace(/\./g, "")
                            .replace(",", ".")
                        )
                        .map(value =>
                          maybeParseFloat(value)
                            .map(number => new Big(number).div(100).toString())
                            .getOrElse(value)
                        )
                        .getOrElse(null);

                    case "CNPJ":
                      return maybeEmpty(valor)
                        .map(s => String(s).trim())
                        .map(formatCNPJ)
                        .getOrElse(null);

                    case "DATA":
                      return maybeEmpty(valor)
                        .map(trim)
                        .chain(date => parseDate(date))
                        .map(date => formatDate(date))
                        .getOrElse(
                          maybeEmpty(value)
                            .map(trim)
                            .getOrElse(null)
                        );

                    default:
                      return maybeEmpty(valor)
                        .map(s => String(s).trim())
                        .getOrElse(null);
                  }
                };

                const onlyNumbers = number =>
                  isNaN(number) ? Maybe.Nothing() : Maybe(number);

                const formataMoeda = (valor, formatacaoResultado) => {
                  const decimals =
                    hasPath(["casasDecimais"], col) && !isNil(col.casasDecimais)
                      ? col.casasDecimais
                      : casasDecimaisDefault;

                  const moneySymbol = code => {
                    const moneySymbols = {
                      BRL: "R$",
                      EUR: "€",
                      USD: "$",
                    };

                    return moneySymbols[code];
                  };

                  return maybeEmpty(valor)
                    .map(parseFloat)
                    .chain(onlyNumbers)
                    .map(number => number.toString())
                    .map(number =>
                      formatNumber(
                        {
                          ...pick(
                            ["decimals", "prefix", "suffix"],
                            formatacaoResultado
                          ),
                          decimals,
                          prefix: maybeEmpty(
                            moneySymbol(formatacaoResultado.moeda)
                          )
                            .map(symbol => `${symbol} `)
                            .getOrElse(null),
                        },
                        number
                      )
                    )
                    .map(removePad)
                    .getOrElse(getter(valor));
                };

                const formataDecimal = (valor, formatacaoResultado) => {
                  const decimals =
                    hasPath(["casasDecimais"], col) && !isNil(col.casasDecimais)
                      ? col.casasDecimais
                      : casasDecimaisDefault;

                  return maybeEmpty(valor)
                    .map(parseFloat)
                    .chain(onlyNumbers)
                    .map(number => number.toString())
                    .map(number =>
                      formatNumber(
                        {
                          ...pick(
                            ["decimals", "prefix", "suffix"],
                            formatacaoResultado
                          ),
                          decimals,
                        },
                        number
                      )
                    )
                    .getOrElse(getter(valor));
                };

                const formataTipo = ({ valor, formatacaoResultado }) => {
                  valor = normalizaValor({ valor, formatacaoResultado });

                  switch (formatacaoResultado.formatacao) {
                    case "PORCENTAGEM":
                    case "PORCENTAGEM_TAXAS_SEM_TEXTOS":
                    case "PORCENTAGEM_TAXAS":
                      return valor;
                    case "DECIMAL":
                      return formataDecimal(
                        escalaFormatacao(valor, pathOr(null, ["escala"], col)),
                        formatacaoResultado
                      );
                    case "MOEDA":
                    case "MOEDA_QUALITATIVA":
                      return formataMoeda(
                        escalaFormatacao(valor, pathOr(null, ["escala"], col)),
                        formatacaoResultado
                      );
                    case "INTEIRO":
                      return valor;
                    case "DATA":
                      return valor;
                    default:
                      return valor;
                  }
                };

                const valorTestado = parseValueOrJson(value);
                // Se veio com JSON, passa pelo normalizaValor e formataTipo (novo formato)
                // Se não, passa variável direto com formatação anterior (pela coluna)
                if (typeof valorTestado === "object" && valorTestado !== null) {
                  return String(formataTipo(valorTestado));
                } else {
                  return valueFormatted || value;
                }
              }
            },
            ...createColumn(getter, col, casasDecimaisDefault, i18n.translate),
          };
        })
        .map((col, i) =>
          i === 0
            ? {
                ...col,
                // Enables checkbox on the first column only if we are not grouping
                checkboxSelection: params =>
                  params.columnApi.getRowGroupColumns().length === 0,
                headerCheckboxSelection: params =>
                  params.columnApi.getRowGroupColumns().length === 0,
                headerCheckboxSelectionFilteredOnly: params =>
                  params.columnApi.getRowGroupColumns().length === 0,
              }
            : col
        )
        .map((col, i) => {
          const valueFormatter = col.valueFormatter;
          if (valueFormatter) valueFormatter.type = cols[i].type;
          return {
            ...col,
            valueFormatter,
          };
        }),
    })
  ),

  withProps(
    ({
      cols: reportCols,
      rows: reportRows,
      identificadores,
      modoApp,
      exibirDatasSemNegociacao,
    }) => {
      if (modoApp === MODO_APP_SERIES) {
        const indiceColunaCNPJ = findIndex(
          col => col.headerName === "CNPJ",
          reportCols
        );

        const ativosComResultadoVazio = getAssetsWithoutResults(
          reportRows,
          identificadores.size + 1
        );

        const linhasFiltradas = filterRamda(row => {
          return (
            isNil(ativosComResultadoVazio[row.key]) &&
            (exibirDatasSemNegociacao ||
              !isEmptyLine(drop(identificadores.size + 1, row.values)))
          );
        }, reportRows);

        const linhasMapeadas = identificadores.has(IDENTIFICADOR_SERIE_CNPJ)
          ? map(linha => {
              linha.values[indiceColunaCNPJ] = maybeEmpty(
                linha.values[indiceColunaCNPJ]
              )
                .map(formatCNPJ)
                .getOrElse(null);

              return linha;
            }, linhasFiltradas)
          : linhasFiltradas;

        return {
          rows: linhasMapeadas,
        };
      } else {
        return {
          rows: reportRows,
        };
      }
    }
  )
);

class Grid extends React.Component<Props & I18n<string>, State> {
  gridApi: any;
  columnApi: any;

  state = {
    currentPage: 1,
    pageCount: 0,
    pageSize: 1000,
    pagination: false,
    selectedAssets: [],
  };

  withGridApi: Function => any = fn => Maybe(this.gridApi).map(fn);

  getCustomMenuAction = (context, menuAction) => {
    switch (menuAction) {
      case "exportaGrafico":
        return {
          name: this.props.translate("Comum.Grafico"),
          action: () => {
            try {
              const ultimaSelecao = this.gridApi.getCellRanges().length - 1;

              const primeiraLinha =
                this.gridApi.getCellRanges()[ultimaSelecao].startRow.rowIndex <=
                this.gridApi.getCellRanges()[ultimaSelecao].endRow.rowIndex
                  ? this.gridApi.getCellRanges()[ultimaSelecao].startRow
                  : this.gridApi.getCellRanges()[ultimaSelecao].endRow;

              const ultimaLinha =
                this.gridApi.getCellRanges()[ultimaSelecao].endRow.rowIndex >
                this.gridApi.getCellRanges()[ultimaSelecao].startRow.rowIndex
                  ? this.gridApi.getCellRanges()[ultimaSelecao].endRow
                  : this.gridApi.getCellRanges()[ultimaSelecao].startRow;

              const selecao = {
                colunas: this.gridApi.getCellRanges()[ultimaSelecao].columns,
                primeiraLinha,
                ultimaLinha,
              };

              const linhas = range(
                selecao.primeiraLinha.rowIndex,
                selecao.ultimaLinha.rowIndex + 1
              );

              const dados = selecao.colunas.map(coluna => {
                return {
                  coluna: coluna.colId,
                  datas: linhas.map(linha =>
                    this.gridApi.getValue(
                      "DATA",
                      this.gridApi.getDisplayedRowAtIndex(linha)
                    )
                  ),
                  formatador: coluna.colDef.valueFormatter.type,
                  medida: coluna.colDef.headerName,
                  valores: linhas.map(linha => {
                    let valor = Number(
                      this.gridApi.getValue(
                        coluna.colId,
                        this.gridApi.getDisplayedRowAtIndex(linha)
                      )
                    );

                    if (coluna.colDef.valueFormatter.type === "percentage") {
                      valor = 100 * valor;
                    }

                    return valor === "" || !valor ? null : Number(valor);
                  }),
                  valoresFormatados: linhas.map(linha =>
                    coluna.colDef.valueFormatter({
                      value: this.gridApi.getValue(
                        coluna.colId,
                        this.gridApi.getDisplayedRowAtIndex(linha)
                      ),
                    })
                  ),
                };
              });

              this.props.openModal("HighchartsModal", { dados: dados });
            } catch {
              this.props.openModal("ModalErro", {
                erros: [
                  this.props.translate(
                    "configuracaoRelatorio.erroGerarGrafico"
                  ),
                ],
              });
            }
          },
        };
      case "copy":
        return {
          name: this.props.translate("Comum.Copiar.Area.Marcada"),
          action: () =>
            this.withGridApi(gridApi => gridApi.copySelectedRangeToClipboard()),
        };

      case "markSelectedAssets":
        return {
          name: this.props.translate("Relatorio.Marcar.Ativos.Selecionados"),
          action: () =>
            this.withGridApi(gridApi => {
              Maybe(context.node).map(node => node.setSelected(true));

              Maybe(gridApi.getCellRanges())
                .map(
                  map(({ startRow, endRow }) =>
                    range(startRow.rowIndex, endRow.rowIndex + 1)
                  )
                )
                .map(
                  map(rows =>
                    rows.map(row => gridApi.getDisplayedRowAtIndex(row))
                  )
                )
                .map(map(rows => rows.map(row => row.setSelected(true))));
            }),
        };

      case "removeAsset":
        return {
          name: this.props.translate("Ativo.Remover"),
          action: () => this.props.onRemoveAsset(context.node.data.key),
        };

      case "sortAsc":
        return {
          name: this.props.translate("Comum.Ordernar.Crescente"),
          action: () =>
            this.withGridApi(gridApi => {
              gridApi.setSortModel([
                {
                  colId: context.column.colId,
                  sort: "asc",
                },
              ]);
            }),
        };

      case "sortDesc":
        return {
          name: this.props.translate("Comum.Ordernar.Decrescente"),
          action: () =>
            this.withGridApi(gridApi => {
              gridApi.setSortModel([
                {
                  colId: context.column.colId,
                  sort: "desc",
                },
              ]);
            }),
        };

      case "selectThisColumn":
        return {
          name: this.props.translate("Relatorio.Selecionar.Toda.Coluna"),
          action: () => {
            this.gridApi.clearRangeSelection();
            return this.withGridApi(gridApi =>
              gridApi.addCellRange({
                columnStart: context.column,
                columnEnd: context.column,
                rowStartIndex: 0,
                rowEndIndex: this.props.rows.length - 1,
              })
            );
          },
        };

      default:
        return null;
    }
  };

  getMainMenuItems = params => [
    this.getCustomMenuAction(params, "sortAsc"),
    this.getCustomMenuAction(params, "sortDesc"),
    "separator",
    "pinSubMenu",
    ...params.defaultItems.filter(equals("valueAggSubMenu")),
    "separator",
    this.getCustomMenuAction(params, "markSelectedAssets"),
    this.getCustomMenuAction(params, "selectThisColumn"),
    "separator",
    this.getCustomMenuAction(params, "copy"),
    "separator",
    "autoSizeThis",
    "autoSizeAll",
    ...(this.props.colHasMenuGroupItem(params.column.colDef.field)
      ? ["separator", "rowGroup"]
      : []),
    "separator",
    "resetColumns",
  ];

  onSearch = (event: *) =>
    this.withGridApi(api => api.setQuickFilter(event.target.value));

  setPaginationInfo = () => {
    this.withGridApi(api =>
      this.setState({
        currentPage: api.paginationGetCurrentPage(),
        pageCount: api.paginationGetTotalPages(),
      })
    );
  };

  onCSVExport = () => this.props.exportReportAsCSV(this.props.reportId);

  onExcelExport = () => this.props.exportReportAsExcel(this.props.reportId);

  onLinkedExcelExport = () => this.props.exportarExcelVinculado(); // Maneira de exportar vinculado como na tela de relatório. Deixando a funcão exportReportAsLinkedExcel inutilizada por hora

  onSaveGroupClick = () => this.props.openModal("saveAssetGroup");

  onSaveGroupSubmit = ({ name, override = false }) =>
    this.props.saveAssetGroup({
      group: this.state.selectedAssets,
      name,
      override,
    });

  onSelectPage = (event: *) => {
    const {
      target: { value },
    } = event;

    event.preventDefault();
    this.withGridApi(api => api.paginationGoToPage(parseInt(value, 10)));
  };

  onSendMailClick = () => this.props.openModal("sendMail");

  onSendMailSubmit = mail => this.props.sendMail(this.props.reportId, mail);

  onPageSizeChange = (event: *) => {
    const value = event.target.value;

    if (!value) {
      this.setState({ pagination: false });
    } else {
      this.withGridApi(api => {
        api.paginationSetPageSize(parseInt(value, 10));
      });
    }
  };

  onPrevPageClick = (event: *) => {
    event.preventDefault();
    this.withGridApi(api => api.paginationGoToPreviousPage());
  };

  onNextPageClick = (event: *) => {
    event.preventDefault();
    this.withGridApi(api => api.paginationGoToNextPage());
  };

  onGridReady = (event: *) => {
    this.gridApi = event.api;
    this.columnApi = event.columnApi;
    this.setPaginationInfo();
    this.restoreGridFromState();
  };

  onGridSelectionChange = () => {
    this.withGridApi(gridApi => {
      this.setState({
        selectedAssets: gridApi.getSelectedNodes().map(node => node.data.key),
      });
    });
  };

  onSelectionRemove = () =>
    this.props.onRemoveAssets(this.state.selectedAssets);

  debouncedSetGridState = debounce(
    (...args) => this.props.saveGridState(...args),
    500
  );

  restoreGridFromState = () => {
    const {
      aggregation,
      filter,
      fixedLeft,
      fixedRight,
      group,
      order,
      sort,
    } = this.props.gridState;

    const convertFilterModel = ({
      filter,
      filterType,
      type,
      operator,
      filters,
      dateFrom,
      dateTo,
    }) => {
      switch (filterType) {
        case "COMPOSITION":
          return {
            operator,
            condition1: filters[0],
            condition2: filters[1],
          };

        case "date":
          return {
            filter,
            filterType,
            type,
            dateFrom,
            dateTo,
          };

        default:
          return {
            filter,
            filterType,
            type,
          };
      }
    };

    const filterModel = filter.reduce(
      (model, { column, ...filter }) => ({
        ...model,
        [column]: convertFilterModel(filter),
      }),
      {}
    );

    const sortModel = sort.reduce(
      (model, [colId, sort]) => [...model, { colId, sort }],
      []
    );

    const fixedColumns = {
      ...fixedLeft.reduce(
        (fixed, colId) => ({
          ...fixed,
          [colId]: "left",
        }),
        {}
      ),
      ...fixedRight.reduce(
        (fixed, colId) => ({
          ...fixed,
          [colId]: "right",
        }),
        {}
      ),
    };

    const groupsIndexes = group.reduce(
      (group, colId, index) => ({
        ...group,
        [colId]: index,
      }),
      {}
    );

    const aggregationsColumns = aggregation.reduce(
      (agg, { column, aggFunc }) => ({
        ...agg,
        [column]: aggFunc,
      }),
      {}
    );

    const isHidden = colId =>
      colId !== ID_COLUNA_GRUPO_AGGRID &&
      !order.find(equals(colId)) &&
      order.length !== 0;

    const originalColumns = new Map(
      (this.props.cols: Array<Object>).map(col => [col.field, col])
    );

    const getAggFuncOrNull = colId => {
      try {
        const col = (originalColumns.get(colId): Object);
        if (col.field === null) {
          return null;
        }

        const grupamento = this.props.colsComMedidas.find(
          c => c.alias === col.field
        ).medida.medida.grupamentoMedida.nome;

        if (grupamento === "QUALITATIVA") {
          return null;
        }

        return col.aggFunc;
      } catch (e) {}

      return null;
    };

    const columns = this.columnApi.getColumnState().map(column => ({
      ...column,
      hide: isHidden(column.colId),
      pinned: fixedColumns[column.colId],
      rowGroupIndex: groupsIndexes[column.colId],
      aggFunc: aggregationsColumns[column.colId]
        ? aggregationsColumns[column.colId]
        : getAggFuncOrNull(column.colId),
    }));

    // NOTE: Esse try está aqui por conta de um bug não resolvido nessa versão
    //       do ag-grid: https://github.com/ag-grid/ag-grid/issues/3334
    try {
      // Caso haja alguma coluna agrupada, ela também precisa ser ordenada corretamente. Para que isso seja possível,
      // é necessário atualizar os grupos na grid antes de restaurar a ordem das colunas.
      this.columnApi.setColumnState(isEmpty(columns) ? [] : columns);
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        console.error("[catched error]: ", e);
      }
    }

    const groupedColumns = this.columnApi.getColumnState();
    const columnById = indexBy(prop("colId"), groupedColumns);

    const [validCols, invalidCols] = partition(
      colId => colId in columnById,
      order.map(colId =>
        colId === ID_COLUNA_GRUPO_AXIS ? ID_COLUNA_GRUPO_AGGRID : colId
      )
    );

    const orderedColumnState = [
      ...validCols.map(colId => columnById[colId]),
      ...groupedColumns.filter(({ colId }) => !includes(colId, order)),
    ];

    if (invalidCols.length > 0) {
      console.warn("Visualização contém colunas inválidas: ", invalidCols);
    }

    this.gridApi.setFilterModel(filterModel);
    this.gridApi.setSortModel(sortModel);

    // NOTE: Esse try está aqui por conta de um bug não resolvido nessa versão
    //       do ag-grid: https://github.com/ag-grid/ag-grid/issues/3334
    try {
      this.columnApi.setColumnState(orderedColumnState);
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        console.error("[catched error]: ", e);
      }
    }

    this.onColumnValueChanged({
      columns: (isEmpty(columns) ? [] : columns).filter(col => col.aggFunc),
    });
  };

  onColumnValueChanged = ({ columns }) => {
    const aggregationColumns = columns.map(({ aggFunc, colId }) => ({
      column: colId,
      aggFunc,
    }));

    this.props.saveGridState("aggregation", aggregationColumns);
  };

  onSaveColumnState = ({ type, columns }) => {
    const { saveGridState } = this.props;
    const columnState = this.columnApi.getColumnState();

    const persistOrder = () =>
      saveGridState(
        "order",
        columnState
          .filter(({ hide }) => !hide)
          .map(prop("colId"))
          .map(colId =>
            colId === ID_COLUNA_GRUPO_AGGRID ? ID_COLUNA_GRUPO_AXIS : colId
          )
      );

    switch (type) {
      case "columnVisible":
      case "columnMoved": {
        persistOrder();
        break;
      }

      case "columnRowGroupChanged": {
        const groups = columns.map(prop("colId"));
        saveGridState("group", groups);
        persistOrder();

        break;
      }

      case "columnPinned": {
        const fixed = pipe(
          groupBy(prop("pinned")),
          map(map(prop("colId")))
        )(columnState);

        saveGridState("fixedRight", fixed["right"] || []);
        saveGridState("fixedLeft", fixed["left"] || []);

        break;
      }

      default:
        break;
    }
  };

  onSortChanged = params => {
    const sortModel = params.api.getSortModel();

    const sort = sortModel.reduce(
      (model, s) => [...model, [s.colId, s.sort]],
      []
    );

    this.props.saveGridState("sort", sort);
  };

  onFilterChanged = params => {
    const filterModel = params.api.getFilterModel();

    const convertFilterModel = filter => {
      if (filter.operator != null) {
        return {
          filterType: "COMPOSITION",
          operator: filter.operator,
          filters: [filter.condition1, filter.condition2],
        };
      }

      switch (filter.filterType) {
        case "formattedNumber": {
          return {
            filterType: "number",
            type: filter.type,
            filter: filter.filter,
          };
        }

        default:
          return filter;
      }
    };

    const filter = toPairs(filterModel)
      .filter(([, filter]) => !isEmpty(filter.filter))
      .map(([colId, filter]) => ({
        column: colId,
        ...convertFilterModel(filter),
      }));

    this.props.saveGridState("filter", filter);
  };

  onFirstDataRendered = () => {
    this.restoreGridFromState();
  };

  menuItems = (params: *) => {
    const modoApp = this.props.modoApp;

    if (modoApp === MODO_APP_SERIES) {
      return [
        this.getCustomMenuAction(params, "exportaGrafico"),
        "separator",
        "copy",
        "copyWithHeaders",
        "separator",
        this.getCustomMenuAction(params, "markSelectedAssets"),
        this.getCustomMenuAction(params, "selectThisColumn"),
        "separator",
        this.getCustomMenuAction(params, "removeAsset"),
        // "separator",         //Removidos para utilização do Highcharts
        // "rangeColumnChart",
        // "rangeBarChart",
        // "rangePieChart",
        // "rangeArea",
      ];
    } else {
      return [
        "copy",
        "copyWithHeaders",
        "separator",
        this.getCustomMenuAction(params, "markSelectedAssets"),
        this.getCustomMenuAction(params, "selectThisColumn"),
        "separator",
        this.getCustomMenuAction(params, "removeAsset"),
        "separator",
        "chartRange",
      ];
    }
  };

  processChartOptions = ({ options, type }) => {
    options.legend.position = "bottom";

    const columns = this.withGridApi(api =>
      api.getCellRanges().flatMap(range => range.columns)
    ).getOrElse([]);

    const series = columns.filter(
      column => column.colDef.chartDataType === "series"
    );
    const types = series.map(column => column.colDef.valueFormatter.type);
    if (types.length > 0 && uniq(types).length >= 1) {
      const valueFormatter = series[0].colDef.valueFormatter;
      const modoApp = this.props.modoApp;

      const Tooltip = ({ color, title, x, y }) => (
        <>
          <div
            className="title"
            style={color ? { color: "white", backgroundColor: color } : {}}
          >
            {title}
          </div>
          <div className="content">
            {x}: {y}
          </div>
        </>
      );

      const DefaultTooltip = ({ format, datum, xKey, yKey, ...props }) => (
        <Tooltip
          {...props}
          x={datum[xKey].toString()}
          y={format({ value: datum[yKey] })}
        />
      );

      const PieTooltip = ({ format, datum, labelKey, angleKey, ...props }) => (
        <Tooltip
          {...props}
          x={datum[labelKey].toString()}
          y={format({ value: datum[angleKey] })}
        />
      );

      switch (type) {
        case "pie":
        case "doughnut": {
          options.seriesDefaults.tooltip.renderer = params =>
            renderToStaticMarkup(
              <PieTooltip format={valueFormatter} {...params} />
            );
          break;
        }

        case "area":
          if (modoApp === MODO_APP_SERIES) {
            options.seriesDefaults.fill.opacity = 0;
            options.legend.item.marker.strokeWidth = 9;
            options.legend.item.marker.size = 5;
            options.yAxis.label.formatter = valueFormatter;
            options.seriesDefaults.tooltip.renderer = params =>
              renderToStaticMarkup(
                <DefaultTooltip format={valueFormatter} {...params} />
              );
          }
          break;

        case "scatter":
        case "bubble":
        case "line":
          options.yAxis.label.formatter = ({ value }) => {
            return series[0].colDef.valueFormatter({ value: value });
          };
          break;

        case "normalizedColumn":
        case "normalizedArea": {
          options.yAxis.label.formatter = ({ value }) =>
            formatNumber({ suffix: " %", decimals: 0 }, value);
          options.seriesDefaults.tooltip.renderer = params =>
            renderToStaticMarkup(
              <DefaultTooltip format={valueFormatter} {...params} />
            );
          break;
        }

        case "normalizedBar":
          options.xAxis.label.formatter = ({ value }) =>
            formatNumber({ suffix: " %", decimals: 0 }, value);
          options.seriesDefaults.tooltip.renderer = params =>
            renderToStaticMarkup(
              <DefaultTooltip format={valueFormatter} {...params} />
            );
          break;

        case "groupedBar":
        case "stackedBar":
          options.xAxis.label.formatter = valueFormatter;
          options.seriesDefaults.tooltip.renderer = params =>
            renderToStaticMarkup(
              <DefaultTooltip format={valueFormatter} {...params} />
            );
          break;

        default: {
          options.yAxis.label.formatter = valueFormatter;
          options.seriesDefaults.tooltip.renderer = params =>
            renderToStaticMarkup(
              <DefaultTooltip format={valueFormatter} {...params} />
            );
          break;
        }
      }
    }

    return options;
  };

  renderGroup = params => {
    const formatter = Maybe(
      this.props.cols.find(propEq("field", params.node.field))
    )
      .chain(col => Maybe(col.valueFormatter))
      .getOrElse(prop("value"));

    const value = formatter(params);

    return value === "" || value == null ? "Sem valor" : value;
  };

  localeTextFunc = (key, defaultValue) => {
    const modoApp = this.props.modoApp;

    if (modoApp === MODO_APP_SERIES) {
      switch (key) {
        //   Nessa versão do ag-grid o gráfico de linhas está bugado na
        // escala do eixo Y. Estamos usando o gráfico de área e retirando
        // a opacidade para imitar o gráfico de linha.
        case "area":
          return "Linha";
        default:
          return this.props.lookup(`ag-grid.${key}`) || defaultValue;
      }
    } else {
      return this.props.lookup(`ag-grid.${key}`) || defaultValue;
    }
  };

  render() {
    const { loaded, rows, cols } = this.props;

    return (
      <div className="Grid">
        <Header onSearch={this.onSearch} />

        <Filters
          currentPage={this.state.currentPage}
          pageCount={this.state.pageCount}
          pagination={this.state.pagination}
          removeFromSelectionDisabled={this.state.selectedAssets.length === 0}
          saveGroupDisabled={this.state.selectedAssets.length === 0}
          openFileDisabled={this.state.selectedAssets.length > 0}
          saveFileDisabled={this.state.selectedAssets.length > 0}
          onCSVExport={this.onCSVExport}
          onExcelExport={this.onExcelExport}
          onLinkedExcelExport={this.onLinkedExcelExport}
          onOpenNewTask={this.props.onOpenNewTask}
          onSaveGroupClick={this.onSaveGroupClick}
          onSaveGroupSubmit={this.onSaveGroupSubmit}
          onSendMailClick={this.onSendMailClick}
          onSendMailSubmit={this.onSendMailSubmit}
          onNextPageClick={this.onNextPageClick}
          onPageSizeChange={this.onPageSizeChange}
          onPrevPageClick={this.onPrevPageClick}
          onSelectPage={this.onSelectPage}
          onSelectionRemove={this.onSelectionRemove}
        />

        <div className="ag-theme-balham Grid__grid">
          {loaded && (
            <AgGridReact
              aggFuncs={aggregationFunctions}
              autoGroupColumnDef={{
                cellRendererParams: {
                  checkbox: false,
                  innerRenderer: this.renderGroup,
                },
              }}
              onFirstDataRendered={this.onFirstDataRendered}
              onColumnValueChanged={this.onColumnValueChanged}
              onColumnVisible={this.onSaveColumnState}
              onColumnPinned={this.onSaveColumnState}
              onColumnResized={this.onSaveColumnState}
              onColumnMoved={this.onSaveColumnState}
              onColumnRowGroupChanged={this.onSaveColumnState}
              processChartOptions={this.processChartOptions}
              onGridReady={this.onGridReady}
              onFilterChanged={this.onFilterChanged}
              onSortChanged={this.onSortChanged}
              filterRow={{ visible: true }}
              filterPanel={{ visible: true }}
              enableRangeSelection
              enableCharts
              rowGroupPanelShow="always"
              getMainMenuItems={this.getMainMenuItems}
              floatingFilter
              suppressPaginationPanel
              suppressRowClickSelection
              pagination={this.state.pagination}
              onPaginationChanged={this.setPaginationInfo}
              paginationPageSize={this.state.pageSize}
              rowSelection="multiple"
              onSelectionChanged={this.onGridSelectionChange}
              multiSortKey="shift"
              headerHeight={70}
              rowHeight={22}
              deltaRowDataMode
              getRowNodeId={data => data.rowId}
              modules={[ClipboardModule]}
              getContextMenuItems={this.menuItems}
              popupParent={document.body}
              defaultColDef={defaultColDef}
              columnDefs={cols}
              rowData={rows}
              localeTextFunc={this.localeTextFunc}
              statusBar={{
                statusPanels: [
                  {
                    statusPanel: "agTotalAndFilteredRowCountComponent",
                    key: "totalAndFilter",
                    align: "left",
                  },
                  { statusPanel: "agSelectedRowCountComponent", align: "left" },
                  {
                    statusPanel: "aggregationStatusBar",
                    align: "right",
                    statusPanelParams: {
                      aggFuncs: [
                        "avg",
                        "median",
                        "count",
                        "min",
                        "max",
                        "minDate",
                        "maxDate",
                        "sum",
                      ],
                    },
                  },
                ],
              }}
              frameworkComponents={{
                aggregationStatusBar: AggregationStatusBar,
                formattedNumberFilter: FormattedNumberFilter,
                proxyFloatingFilter: ProxyFloatingFilter,
                // NOTE: We are now using the browser default date picker due to
                //       versions inconsistency (just like the monitoramento project)
                // agDateInput: props => (
                //   <DatePicker translate={this.props.translate} {...props} />
                // ),
              }}
            />
          )}
        </div>
      </div>
    );
  }
}

export default enhance(Grid);
